import { Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import AddButton, { ApproveButton, DeclineButton } from "../../buttons/button";
import InputField from "../../forms/input-field";
import SelectField from "../../forms/select-field";
import DatePickerInput from "../../forms/date-picker";
import TabsComponent from "../../helper-components/tabs-component";
import MapsAndPhotos from "./maps-photos";
import StoreShopAddressTab from "./shop-address";
import StoreDetailsTab from "./store-details";
import StoreRemarksTab from "./remarks";
import { useDispatch } from "react-redux";
import {
  StoreAddressCreateAction,
  StoreAddressUpdateAction,
  StoreDrugCreateAction,
  StoreDrugUpdateAction,
} from "../../../redux/actions/store-action";
import { api } from "../../../redux/apis/api";
import successToast from "../../helper-components/success-toast";
import errorToast from "../../helper-components/error-toast";

const StoreAddressTab = ({
  setId,
  setStoreData,
  storeData,
  initialValue,
  id,
  permissionFn = () => {},
  disabled = false,
}) => {
  const [currentTab, setCurrentTab] = useState("Shop Address");
  const [storeTypeOptions, setStoreTypeOptions] = useState([]);
  const [storeTypeMap, setStoreTypeMap] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {
    api
      .get("/fofo/storeType/getAll")
      .then((res) => {
        const list = res.data?.data ?? [];
        console.log("Store types fetched:", list);
        const options = list.map((t) => ({ label: t.store_type, value: t.store_type }));
        const map = {};
        list.forEach((t) => { map[t.store_type] = t; });
        setStoreTypeOptions(options);
        setStoreTypeMap(map);
      })
      .catch((err) => {
        console.error("Failed to fetch store types:", err?.response?.data ?? err?.message);
      });
  }, []);
  const storedType = storeTypeMap[initialValue?.store_type] ?? null;
  const initialValues = {
    ...(id && { store_id: id }), // Include store_id only if id is present
    store_name: initialValue?.store_name ?? "",
    store_code: initialValue?.store_code ?? "",
    store_type: initialValue?.store_type ?? "",
    base_commission: initialValue?.base_commission ?? storedType?.base_commision ?? "",
    additional_margin: initialValue?.additional_margin ?? storedType?.additional_margin ?? "",
    approx_capex: initialValue?.approx_capex ?? storedType?.capex ?? "",
    franchise_fee: initialValue?.franchise_fee ?? storedType?.franchidse_fee ?? "",
    lock_in_period: initialValue?.lock_in_period ?? storedType?.lock_in_period ?? "",
    carpet_area: initialValue?.carpet_area ?? initialValue?.corpet_area ?? "",
    shop_length: initialValue?.shop_length ?? "",
    shop_width: initialValue?.shop_width ?? "",
    shop_height: initialValue?.shop_height ?? "",
    shutter_width: initialValue?.shutter_width ?? "",
    shutter_height: initialValue?.shutter_height ?? "",
    washroom_availability: initialValue?.washroom_availability ?? null,
    is_dl_available: initialValue?.is_dl_available ?? null,
    dl_valid_date: initialValue?.dl_valid_date ?? initialValue?.dl_valid_till_date ?? "",
    store_360_video: initialValue?.store_360_video ?? initialValue?.video_url ?? "",
    relationship_manager: initialValue?.relationship_manager ?? "",
    reapproval_remarks: initialValue?.reapproval_remarks ?? "",
    addres_remarks: initialValue?.addres_remarks ?? "",
    is_approve_location: initialValue?.is_approve_location ?? false,
    is_reject_location: initialValue?.is_reject_location ?? false,
    shop_address: initialValue?.shop_address ?? "",
    shop_area: initialValue?.shop_area ?? "",
    shop_landmark: initialValue?.shop_landmark ?? "",
    shop_town_or_village: initialValue?.shop_town_or_village ?? "",
    shop_city: initialValue?.shop_city ?? "",
    shop_state: initialValue?.shop_state ?? "",
    shop_pin_code: initialValue?.shop_pin_code ?? "",
    shop_country_region: initialValue?.shop_country_region ?? "",
    shop_region: initialValue?.shop_region ?? "", // new

    property_owned_type: initialValue?.property_owned_type ?? null,
    store_rent_per_month: initialValue?.store_rent_per_month ?? null,
    store_duration_concurrently:
      initialValue?.store_duration_concurrently ?? "",
    store_sale_per_day: initialValue?.store_sale_per_day ?? null,
    store_medicine_sale: initialValue?.store_medicine_sale ?? null,
    store_otc_sale: initialValue?.store_otc_sale ?? null,
    store_latitude: initialValue?.store_latitude ?? "",
    store_longitude: initialValue?.store_longitude ?? "",
    shop_images: initialValue?.shop_images ?? [],
    tab_remarks: initialValue?.tab_remarks ?? "",
  };
  const validationSchema = Yup.object().shape({
    store_type: Yup.string().required("Store Type is required"),
    relationship_manager: Yup.string().required(
      "Relationship Manager is required"
    ),
    shop_address: Yup.string().required("Shop Address is required"),
    shop_area: Yup.string().required("Shop Area is required"),
    shop_landmark: Yup.string().required("Shop Landmark is required"),
    shop_town_or_village: Yup.string().required("Town or Village is required"),
    shop_city: Yup.string().required("City is required"),
    shop_state: Yup.string().required("State is required"),
    shop_pin_code: Yup.string()
      .required("Pin Code is required")
      .matches(/^[1-9][0-9]{5}$/, "Invalid Pin Code"),
    shop_country_region: Yup.string().required("Country is required"),
    shop_region: Yup.string().required("Region is required"),

    store_rent_per_month: Yup.number().required("Rent Per Month is required"),
    store_duration_concurrently: Yup.string().required(
      "Store Duration Concurrently is required"
    ),
    store_sale_per_day: Yup.number().required("Store Sale Per Day is required"),
    store_medicine_sale: Yup.number().required("Medicine Sale is required"),
    store_otc_sale: Yup.number().required("OTC Sale is required"),
    store_latitude: Yup.string().required("Store Latitude is required"),
    store_longitude: Yup.string().required("Store Longitude is required"),
    shop_images: Yup.array()
      .min(2, "At least 2 images are required")
      .required("At least 2 images are required"),
  });

  const onSubmit = (values, actions) => {
    const dlValues = {
      is_dl_available: values.is_dl_available,
      dl_valid_date: values.dl_valid_date,
    };
    const isUpdate = !!id;

    const autoPopulatedFields = [
      "base_commission", "additional_margin", "approx_capex",
      "franchise_fee", "lock_in_period",
      "shop_length", "shop_width", "shop_height",
      "shutter_width", "shutter_height",
      "washroom_availability", "is_dl_available", "dl_valid_date",
      "store_360_video",
    ];
    const cleanedValues = { ...values };

    autoPopulatedFields.forEach((field) => delete cleanedValues[field]);

    if ("carpet_area" in cleanedValues) {
      cleanedValues.corpet_area = cleanedValues.carpet_area;
      delete cleanedValues.carpet_area;
    }

    const franchiseFee = values.franchise_fee;
    const actionType = isUpdate ? StoreAddressUpdateAction : StoreAddressCreateAction;
    dispatch(actionType(cleanedValues, (data) => onSuccess(data, franchiseFee, dlValues, isUpdate), onError));
  };

  const onSuccess = (data, franchiseFee, dlValues, isUpdate) => {
    const storeId = data?.data?.store_id ?? id;
    setStoreData({
      ...storeData,
      address: { ...data?.data, franchise_fee: franchiseFee },
    });
    successToast(data?.message);
    setId(storeId);

    if (storeId) {
      const dlPayload = { store_id: storeId, ...dlValues };
      const dlAction = isUpdate ? StoreDrugUpdateAction : StoreDrugCreateAction;
      dispatch(dlAction(dlPayload, () => {}, () => {}));
    }
  };
  const onError = (data) => {
    console.log("Address tab error:", data);
    errorToast(data?.message || data?.data?.message || "Something went wrong");
  };

  const [textToCopy, setTextToCopy] = useState(id);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      successToast("Text copied to clipboard!");
    } catch (err) {
      errorToast("Failed to copy text:", err);
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      // validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue, isSubmitting, errors, submitForm }) => {
        return (
          <Form style={{ height: "calc(100% - 5rem)" }} className="pt-5">
            <div className="flex flex-col justify-between h-full ">
              <div>
                <div className="w-full 2xl:w-[40%] xl:w-[50%] lg:w-[60%] px-5 xl:px-10">
                  {id && (
                    <div className="flex items-start gap-2 w-full">
                      <InputField
                        defaultInputClass="inputField focus_outline__none border-0"
                        containerClass="w-full"
                        isDisabled={true}
                        type="text"
                        name="store_id"
                        placeholder="Enter Store Type"
                        label="POS Integration Id"
                        // isRequired={true}
                        labelWidth="10.5rem"
                      />
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="rounded-md px-4 py-3 text-white bg-027A48 text-sm"
                      >
                        Copy
                      </button>
                    </div>
                  )}
                  <InputField
                    isDisabled={disabled}
                    type="text"
                    name="store_name"
                    placeholder="Enter Store Name"
                    label="Store Name"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="text"
                    name="store_code"
                    placeholder="Enter Store Code"
                    label="Store Code"
                    labelWidth="10.5rem"
                  />
                  <SelectField
                    isDisabled={disabled}
                    placeholder="Select Store Type"
                    options={storeTypeOptions}
                    label="Store Type"
                    id="store_type"
                    name="store_type"
                    isSearchable={true}
                    errMsgContainerClass="ps-2"
                    labelWidth="10.5rem"
                    handleSelected={(selected) => {
                      const t = storeTypeMap[selected?.value];
                      if (t) {
                        setFieldValue("base_commission", t.base_commision ?? "");
                        setFieldValue("additional_margin", t.additional_margin ?? "");
                        setFieldValue("approx_capex", t.capex ?? "");
                        setFieldValue("franchise_fee", t.franchidse_fee ?? "");
                        setFieldValue("lock_in_period", t.lock_in_period ?? "");
                      }
                    }}
                  />
                  <InputField
                    isDisabled={true}
                    type="text"
                    name="base_commission"
                    placeholder="Auto-filled from Store Type"
                    label="Base Commission"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={true}
                    type="text"
                    name="additional_margin"
                    placeholder="Auto-filled from Store Type"
                    label="Additional Margin"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={true}
                    type="text"
                    name="approx_capex"
                    placeholder="Auto-filled from Store Type"
                    label="Approx Capex"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={true}
                    type="text"
                    name="franchise_fee"
                    placeholder="Auto-filled from Store Type"
                    label="Franchise Fee"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={true}
                    type="text"
                    name="lock_in_period"
                    placeholder="Auto-filled from Store Type"
                    label="Lock-in Period"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="number"
                    name="carpet_area"
                    placeholder="Enter Carpet Area"
                    label="Carpet Area"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="number"
                    name="shop_length"
                    placeholder="Enter Shop Length"
                    label="Shop Length"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="number"
                    name="shop_width"
                    placeholder="Enter Shop Width"
                    label="Shop Width"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="number"
                    name="shop_height"
                    placeholder="Enter Shop Height"
                    label="Shop Height"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="number"
                    name="shutter_width"
                    placeholder="Enter Shutter Width"
                    label="Shutter Width"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="number"
                    name="shutter_height"
                    placeholder="Enter Shutter Height"
                    label="Shutter Height"
                    labelWidth="10.5rem"
                  />
                  <SelectField
                    isDisabled={disabled}
                    placeholder="Select Washroom Availability"
                    options={yesNoOptions}
                    label="Washroom Availability"
                    id="washroom_availability"
                    name="washroom_availability"
                    labelWidth="10.5rem"
                  />
                  <SelectField
                    isDisabled={disabled}
                    placeholder="Select DL Availability"
                    options={yesNoOptions}
                    label="Is DL Available"
                    id="is_dl_available"
                    name="is_dl_available"
                    labelWidth="10.5rem"
                  />
                  <div className="flex items-center mb-2">
                    <span
                      style={{ minWidth: "10.5rem" }}
                      className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]"
                    >
                      DL Valid Date
                    </span>
                    <DatePickerInput
                      placeholder="Select DL Valid Date"
                      value={values.dl_valid_date}
                      onChange={(date) =>
                        setFieldValue("dl_valid_date", date)
                      }
                    />
                  </div>
                  <InputField
                    isDisabled={disabled}
                    type="text"
                    name="relationship_manager"
                    placeholder="Enter Relationship Manager"
                    label="Relationship Manager"
                    // isRequired={true}
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="text"
                    name="reapproval_remarks"
                    placeholder="Enter Re-approval Remarks"
                    label="Re-approval Remarks"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="text"
                    name="addres_remarks"
                    placeholder="Enter Remarks"
                    label="Remarks"
                    labelWidth="10.5rem"
                  />{" "}
                  {id && (
                    <div className="flex gap-5">
                      <ApproveButton
                        onclkFn={async () => {
                          await setFieldValue("is_approve_location", true);
                          if (values?.is_reject_location) {
                            await setFieldValue("is_reject_location", false);
                          }
                          submitForm();
                        }}
                        type="button"
                        text={
                          values?.is_approve_location ? "Approved" : "Approve"
                        }
                      />
                      <DeclineButton
                        onclkFn={async () => {
                          await setFieldValue("is_reject_location", true);
                          if (values?.is_approve_location) {
                            await setFieldValue("is_approve_location", false);
                          }
                          submitForm();
                        }}
                        type="button"
                        text={
                          values?.is_reject_location ? "Declined" : "Decline"
                        }
                      />
                    </div>
                  )}
                </div>
                <TabsComponent
                  handleClickfn={(item) => {
                    setCurrentTab(item);
                  }}
                  itemTabs={itemTabs}
                  setCurrentTab={setCurrentTab}
                  currentTab={currentTab}
                />
                {currentTab === "Shop Address" && (
                  <StoreShopAddressTab disabled={disabled} />
                )}
                {currentTab === "Store Rent" && (
                  <StoreDetailsTab disabled={disabled} id={id} />
                )}{" "}
                {currentTab === "Maps & Photos" && (
                  <MapsAndPhotos disabled={disabled} />
                )}
                {currentTab === "Remarks" && (
                  <StoreRemarksTab disabled={disabled} />
                )}
              </div>
              {!disabled && (
                <div
                  style={{
                    bottom: "0",
                  }}
                  className="flex justify-end px-10 mb-5x sticky bg-white py-4 invoice-bottom-container"
                >
                  <AddButton
                    onclkFn={() => {
                      if (Object.keys(errors).length !== 0) {
                        const currentIndex = itemTabs.indexOf(currentTab);
                        let nextTab;

                        if (currentIndex !== -1) {
                          // Go to the next tab based on the current tab index
                          nextTab =
                            currentIndex === itemTabs.length - 1
                              ? itemTabs[0] // Loop back to the first tab if the last tab is reached
                              : itemTabs[currentIndex + 1];

                          setCurrentTab(nextTab);
                        }
                      }
                    }}
                    prefixIcon={false}
                    text="Save"
                    type="submit"
                  />
                </div>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default StoreAddressTab;
const itemTabs = ["Shop Address", "Store Rent", "Maps & Photos", "Remarks"];
const yesNoOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];
